import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Upload,
  FileText,
  Brain,
  Check,
  CheckCircle,
  CheckCircle2,
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Loader2,
  Mail,
  Video,
  Copy,
  Download,
  Filter,
  RefreshCw,
  Clock,
  ArrowLeft,
  ArrowRight,
  GraduationCap,
  Briefcase,
  Rocket,
  Globe2,
  Crown,
  Wallet,
  Home,
  TrendingUp,
  AlertCircle,
  LayoutGrid,
  List,
  FileUp,
  Layers,
  Zap,
  Calendar,
  Search,
  Tag,
  Plus,
  Play,
  Square,
  RotateCcw,
  User,
  Newspaper,
  Settings,
  HelpCircle,
} from 'lucide-react';

// 8 audience types
const AUDIENCE_TYPES = [
  { id: 'student', name: 'Students & Job Seekers', icon: GraduationCap, gradient: 'from-blue-500 to-cyan-500', emoji: '🎓' },
  { id: 'newbie', name: 'New Professionals', icon: Briefcase, gradient: 'from-emerald-500 to-teal-500', emoji: '💼' },
  { id: 'startup', name: 'Startups', icon: Rocket, gradient: 'from-orange-500 to-amber-500', emoji: '🚀' },
  { id: 'global', name: 'Global Investors', icon: Globe2, gradient: 'from-indigo-500 to-purple-500', emoji: '🌏' },
  { id: 'ceo', name: 'Corporate CEOs', icon: Crown, gradient: 'from-yellow-500 to-orange-500', emoji: '👔' },
  { id: 'finance', name: 'Financial Products', icon: Wallet, gradient: 'from-green-500 to-emerald-500', emoji: '💰' },
  { id: 'realestate', name: 'Real Estate', icon: Home, gradient: 'from-rose-500 to-pink-500', emoji: '🏠' },
  { id: 'stock', name: 'Stock Investors', icon: TrendingUp, gradient: 'from-violet-500 to-purple-500', emoji: '📈' },
];

// Workflow steps
const STEPS = [
  { id: 1, label: 'Load Articles' },
  { id: 2, label: 'Filter Articles' },
  { id: 3, label: 'Select Articles' },
  { id: 4, label: 'Generate Content' },
  { id: 5, label: 'Results' },
];

const DEFAULT_EXCLUDE_CATEGORIES = ['Politics', 'Sports', 'Entertainment', 'Culture', 'Obituary', 'Health'];
const DEFAULT_EXCLUDE_KEYWORDS = ['[obituary]', '[personnel]', '[healthy time]', "[today's memo]"];

// Elapsed time display component
function ElapsedTimer({ running }) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (!running) return;
    startRef.current = Date.now();
    setElapsed(0);
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [running]);

  if (!running && elapsed === 0) return null;
  const min = Math.floor(elapsed / 60);
  const sec = elapsed % 60;
  return (
    <span className="tabular-nums text-text-400">
      {min > 0 ? `${min}m ` : ''}{sec}s
    </span>
  );
}

// Pulse dot animation
function PulseDots() {
  return (
    <span className="inline-flex items-center gap-1 ml-2">
      <span className="w-1.5 h-1.5 rounded-full bg-sedaily-500 animate-[pulse_1.4s_ease-in-out_infinite]" />
      <span className="w-1.5 h-1.5 rounded-full bg-sedaily-500 animate-[pulse_1.4s_ease-in-out_0.2s_infinite]" />
      <span className="w-1.5 h-1.5 rounded-full bg-sedaily-500 animate-[pulse_1.4s_ease-in-out_0.4s_infinite]" />
    </span>
  );
}

// Date utilities
const getKoreaTodayDate = () => {
  const now = new Date();
  const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
  return koreaTime.toISOString().split('T')[0];
};

const getRecentDates = (days) => {
  const dates = [];
  for (let i = 0; i < days; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const value = date.toISOString().split('T')[0];
    const label = i === 0 ? 'Today' : i === 1 ? 'Yesterday' : `${date.getMonth() + 1}/${date.getDate()}`;
    dates.push({ value, label });
  }
  return dates;
};

const formatDisplayDate = (dateStr) => {
  const date = new Date(dateStr);
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

const PrismCanvas = () => {
  const navigate = useNavigate();
  const stopFlag = useRef(false);

  // State management
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1 - Article load
  const [selectedDate, setSelectedDate] = useState(getKoreaTodayDate());
  const [availableDates] = useState(getRecentDates(14));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [articleInput, setArticleInput] = useState('');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Step 2 - Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('time');
  const [filterType, setFilterType] = useState('all');
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [startTime, setStartTime] = useState('14:00');
  const [endTime, setEndTime] = useState('20:00');
  const [useTimeFilter, setUseTimeFilter] = useState(false);
  const [excludeCategories, setExcludeCategories] = useState(DEFAULT_EXCLUDE_CATEGORIES);
  const [excludeKeywords, setExcludeKeywords] = useState(DEFAULT_EXCLUDE_KEYWORDS);
  const [newKeyword, setNewKeyword] = useState('');

  // Step 3 - Selection
  const [selectedTargets, setSelectedTargets] = useState(AUDIENCE_TYPES.map(t => t.id));
  const [classifyStatus, setClassifyStatus] = useState('idle');
  const [classificationResults, setClassificationResults] = useState({});
  const [activeClassifyTarget, setActiveClassifyTarget] = useState(null);

  // Step 4 - Generation
  const [generateStatus, setGenerateStatus] = useState('idle');
  const [generationResults, setGenerationResults] = useState({});
  const [activeGenTarget, setActiveGenTarget] = useState(null);

  // Step 5 - Results
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [contentTab, setContentTab] = useState('longform');
  const [copied, setCopied] = useState(false);

  // Common
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [error, setError] = useState(null);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Article parsing
  const parseArticles = (text) => {
    const lines = text.split(/\n{2,}|\r\n{2,}/);
    return lines
      .map((line, index) => ({
        id: `article-${index}`,
        content: line.trim(),
        title: line.trim().slice(0, 80) + (line.length > 80 ? '...' : ''),
        date: selectedDate,
        time: `${14 + Math.floor(index / 50)}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        author: ['Reporter Kim', 'Reporter Lee', 'Reporter Park', 'Reporter Choi'][index % 4],
        categories: [{ name: ['Economy', 'Finance', 'Industry', 'Real Estate', 'Stocks'][index % 5] }],
        paperNumber: index % 3 === 0 ? Math.floor(index / 10) + 1 : 0,
      }))
      .filter(article => article.content.length > 20);
  };

  // Filtered articles
  const filteredArticles = useMemo(() => {
    return articles
      .filter(article => {
        // Type filter
        if (filterType === 'paper' && !(article.paperNumber > 0)) return false;
        if (filterType === 'online' && article.paperNumber > 0) return false;

        // Time filter
        if (useTimeFilter && article.time) {
          if (article.time < startTime || article.time > endTime) return false;
        }

        // Exclude categories
        if (excludeCategories.length > 0 && article.categories?.length > 0) {
          const cats = article.categories.map(c => c.name?.split(',')[0]?.trim()).filter(Boolean);
          if (cats.some(cat => excludeCategories.includes(cat))) return false;
        }

        // Exclude keywords
        if (excludeKeywords.length > 0 && article.title) {
          if (excludeKeywords.some(kw => article.title.includes(kw))) return false;
        }

        // Search
        if (!debouncedSearch) return true;
        const q = debouncedSearch.toLowerCase();
        return article.title?.toLowerCase().includes(q) ||
               article.author?.toLowerCase().includes(q) ||
               article.content?.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (sortBy === 'paper') return (a.paperNumber || 9999) - (b.paperNumber || 9999);
        if (sortBy === 'time') return (b.time || '').localeCompare(a.time || '');
        return a.title?.localeCompare(b.title || '') || 0;
      });
  }, [articles, filterType, useTimeFilter, startTime, endTime, excludeCategories, excludeKeywords, debouncedSearch, sortBy]);

  // Category stats
  const categoryStats = useMemo(() => {
    const stats = {};
    let paperCount = 0, onlineCount = 0;
    filteredArticles.forEach(a => {
      if (a.paperNumber > 0) paperCount++; else onlineCount++;
      const cats = a.categories?.map(c => c.name?.split(',')[0]?.trim()).filter(Boolean) || [];
      [...new Set(cats)].forEach(cat => { stats[cat] = (stats[cat] || 0) + 1; });
    });
    return { categories: Object.entries(stats).sort((a, b) => b[1] - a[1]), paperCount, onlineCount };
  }, [filteredArticles]);

  const activeFilterCount = (useTimeFilter ? 1 : 0) + (excludeCategories.length > 0 ? 1 : 0) + (excludeKeywords.length > 0 ? 1 : 0);

  // Step 1: Load articles
  const handleUpload = () => {
    if (!articleInput.trim()) {
      setError('Please enter articles.');
      return;
    }
    const parsed = parseArticles(articleInput);
    if (parsed.length === 0) {
      setError('No valid articles found.');
      return;
    }
    setArticles(parsed);
    setError(null);
    setCurrentStep(2);
  };

  // Step 3: AI classification
  const toggleTarget = (id) => setSelectedTargets(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
  const toggleAllTargets = () => setSelectedTargets(prev => prev.length === AUDIENCE_TYPES.length ? [] : AUDIENCE_TYPES.map(t => t.id));

  const runClassification = async () => {
    if (selectedTargets.length === 0) { setError('Please select at least one target'); return; }
    stopFlag.current = false;
    setClassifyStatus('running');
    setClassificationResults({});

    const targetList = AUDIENCE_TYPES.filter(t => selectedTargets.includes(t.id));

    try {
      for (let i = 0; i < targetList.length; i++) {
        if (stopFlag.current) { setClassifyStatus('stopped'); setActiveClassifyTarget(null); return; }
        const target = targetList[i];
        setActiveClassifyTarget(target.id);

        // Simulation (replace with real API)
        await new Promise(resolve => setTimeout(resolve, 1500));

        const count = Math.floor(Math.random() * 8) + 3;
        const shuffled = [...filteredArticles].sort(() => Math.random() - 0.5);
        const selectedArts = shuffled.slice(0, Math.min(count, filteredArticles.length));

        setClassificationResults(prev => ({
          ...prev,
          [target.id]: {
            selectedIds: selectedArts.map(a => a.id),
            selectedArticles: selectedArts,
          }
        }));
      }
      setActiveClassifyTarget(null);
      setClassifyStatus('done');
    } catch (err) {
      setClassifyStatus('idle');
      setError('An error occurred during classification.');
    }
  };

  // Step 4: Content generation
  const runGeneration = async () => {
    stopFlag.current = false;
    setGenerateStatus('running');
    setGenerationResults({});

    const targetList = AUDIENCE_TYPES.filter(t => selectedTargets.includes(t.id) && classificationResults[t.id]?.selectedIds?.length > 0);

    try {
      for (let i = 0; i < targetList.length; i++) {
        if (stopFlag.current) { setGenerateStatus('stopped'); setActiveGenTarget(null); return; }
        const target = targetList[i];
        const data = classificationResults[target.id];
        setActiveGenTarget(target.id);

        // Simulation (replace with real API)
        await new Promise(resolve => setTimeout(resolve, 2000));

        setGenerationResults(prev => ({
          ...prev,
          [target.id]: {
            longform: `# Today's News for ${target.name}\n\n## Top Stories\n\n${data.selectedArticles.length} key articles selected for today.\n\n${data.selectedArticles.slice(0, 3).map((a, i) => `### ${i + 1}. ${a.title}\n\n${a.content.slice(0, 200)}...\n`).join('\n')}\n\n---\nSedaily AI Labs`,
            aeo: `## Q: What news should ${target.name} know today?\n\nA: Here are ${data.selectedArticles.length} key stories for today.\n\n${data.selectedArticles.slice(0, 3).map((a, i) => `**${i + 1}. ${a.title}**\n${a.content.slice(0, 100)}...\n`).join('\n')}`,
            selectedIds: data.selectedIds,
            selectedArticles: data.selectedArticles,
          }
        }));
      }
      setActiveGenTarget(null);
      setGenerateStatus('done');
      setCurrentStep(5);
    } catch (err) {
      setGenerateStatus('idle');
      setError('An error occurred during content generation.');
    }
  };

  const handleStop = () => { stopFlag.current = true; };

  // Utilities
  const copyArticle = (article, e) => {
    e?.stopPropagation();
    navigator.clipboard.writeText(`Title: ${article.title}\nReporter: ${article.author || '-'}\n\n${article.content}`);
  };

  const handleCopyContent = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const addKeyword = () => {
    if (newKeyword.trim() && !excludeKeywords.includes(newKeyword.trim())) {
      setExcludeKeywords(prev => [...prev, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const resetAll = () => {
    setCurrentStep(1);
    setArticles([]);
    setArticleInput('');
    setClassificationResults({});
    setGenerationResults({});
    setClassifyStatus('idle');
    setGenerateStatus('idle');
    setSelectedTarget(null);
    setActiveClassifyTarget(null);
    setActiveGenTarget(null);
  };

  const classifiedCount = Object.keys(classificationResults).length;
  const generatedCount = Object.keys(generationResults).length;
  const currentResult = selectedTarget ? generationResults[selectedTarget] : null;
  const currentTargetInfo = AUDIENCE_TYPES.find(t => t.id === selectedTarget);

  return (
    <div className="min-h-screen bg-bg-100">
      {/* Left sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-60 bg-bg-200/80 border-r border-bg-300/50 flex flex-col z-40">
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-bg-300/50 bg-bg-200/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sedaily-500 to-sedaily-600 flex items-center justify-center shadow-lg shadow-sedaily-500/30">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-text-100">PRISM</h1>
              <span className="text-[10px] text-text-500">AI Newsletter Generator</span>
            </div>
          </div>
        </div>

        {/* Step navigation */}
        <nav className="flex-1 py-6 px-4 space-y-2">
          <p className="text-xs font-medium text-text-500 uppercase tracking-wider mb-4 px-2">Workflow</p>
          {STEPS.map((step, idx) => {
            const canClick = step.id < currentStep || (step.id === 5 && generateStatus === 'done');
            const isActive = step.id === currentStep;
            const isDone = step.id < currentStep;
            return (
              <button
                key={step.id}
                onClick={() => canClick && setCurrentStep(step.id)}
                disabled={!canClick && !isActive}
                className={`
                  w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all
                  ${isActive ? 'bg-sedaily-500 text-white shadow-lg shadow-sedaily-500/30' : ''}
                  ${isDone ? 'text-text-200 hover:bg-bg-300/50 cursor-pointer' : ''}
                  ${!isActive && !isDone ? 'text-text-500 cursor-default opacity-50' : ''}
                `}
              >
                <span className={`
                  w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-all
                  ${isActive ? 'bg-white/20 text-white' : ''}
                  ${isDone ? 'bg-emerald-500/20 text-emerald-400' : ''}
                  ${!isActive && !isDone ? 'bg-bg-300/50 text-text-500' : ''}
                `}>
                  {isDone ? <Check className="w-4 h-4" /> : step.id}
                </span>
                {step.label}
              </button>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="p-4 border-t border-bg-300/50">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-text-400 hover:bg-bg-300/50 hover:text-text-200 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Labs
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-60 min-h-screen">
        {/* Main header */}
        <header className="sticky top-0 z-30 bg-bg-100/95 backdrop-blur-xl border-b border-bg-300/50">
          <div className="px-8 h-14 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-text-100">{STEPS[currentStep - 1]?.label}</h2>
              {articles.length > 0 && (
                <span className="px-2.5 py-1 rounded-lg bg-sedaily-500/10 text-sedaily-500 text-xs font-medium">
                  {filteredArticles.length}/{articles.length} articles
                </span>
              )}
            </div>

            {/* Date picker */}
            <div className="relative">
              <button
                onClick={() => currentStep === 1 && setShowDatePicker(!showDatePicker)}
                disabled={currentStep !== 1}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Calendar className="w-4 h-4" />
                {formatDisplayDate(selectedDate)}
                {currentStep === 1 && <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {showDatePicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowDatePicker(false)} />
                  <div className="absolute right-0 mt-1 w-48 bg-bg-200 rounded-xl shadow-xl border border-bg-300/30 py-1 z-20">
                    {availableDates.map(d => (
                      <button
                        key={d.value}
                        onClick={() => { setSelectedDate(d.value); setShowDatePicker(false); }}
                        className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                          d.value === selectedDate ? 'text-sedaily-500 bg-sedaily-500/10 font-medium' : 'text-text-300 hover:bg-bg-300/50'
                        }`}
                      >
                        {d.label} ({d.value})
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Error message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="max-w-5xl mx-auto px-6 pt-4"
            >
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1">{error}</span>
                <button onClick={() => setError(null)} className="p-1 hover:bg-red-500/10 rounded transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ========== STEP 1: Load Articles ========== */}
        {currentStep === 1 && (
          <div className="flex items-center justify-center min-h-[calc(100vh-56px)]">
            <div className="w-full max-w-2xl px-8">
              <div className="text-center mb-10">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-sedaily-500 to-sedaily-600 flex items-center justify-center mx-auto mb-5 shadow-xl shadow-sedaily-500/25">
                  <FileUp className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-text-100 mb-2">Upload Articles</h2>
                <p className="text-text-400">Paste articles for {formatDisplayDate(selectedDate)}</p>
              </div>

              <div className="bg-bg-200/50 rounded-2xl border border-bg-300/50 p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Layers className="w-4 h-4 text-text-400" />
                    <span className="text-text-400">Detected articles:</span>
                    <span className={`font-semibold ${articleInput ? 'text-sedaily-500' : 'text-text-400'}`}>
                      {articleInput ? parseArticles(articleInput).length : 0}
                    </span>
                  </div>
                  <span className="text-xs text-text-500">{articleInput.length.toLocaleString()} chars</span>
                </div>

                <textarea
                  value={articleInput}
                  onChange={(e) => setArticleInput(e.target.value)}
                  placeholder="Paste articles here. Separate each article with a blank line."
                  className="w-full h-64 px-4 py-4 bg-bg-100 border border-bg-300/50 rounded-xl text-text-100 placeholder:text-text-500/70 focus:outline-none focus:ring-2 focus:ring-sedaily-500/50 focus:border-sedaily-500/50 resize-none font-mono text-sm transition-all"
                />

                <div className="flex items-center justify-end mt-5">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleUpload}
                    disabled={!articleInput.trim()}
                    className={`
                      px-8 py-3 rounded-xl font-medium flex items-center gap-2 transition-all
                      ${articleInput.trim()
                        ? 'bg-gradient-to-r from-sedaily-500 to-sedaily-600 text-white shadow-lg shadow-sedaily-500/25 hover:shadow-xl'
                        : 'bg-bg-300/50 text-text-500 cursor-not-allowed'
                      }
                    `}
                  >
                    <Play className="w-4 h-4" />
                    Load Articles
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== STEP 2: Filter Articles ========== */}
        {currentStep === 2 && (
          <div>
            {/* Filter bar */}
            <div className="sticky top-14 z-20 bg-bg-100/90 backdrop-blur-xl border-b border-bg-300/30">
              <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search title, reporter, content..."
                    className="w-full pl-9 pr-4 py-2 text-sm bg-bg-200/50 border border-bg-300/30 rounded-xl focus:bg-bg-200 focus:border-sedaily-500/50 focus:outline-none transition-colors text-text-100 placeholder:text-text-500"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-500 hover:text-text-300">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Type filter */}
                <div className="flex items-center gap-1 text-sm">
                  {[{ value: 'all', label: 'All' }, { value: 'paper', label: 'Print' }, { value: 'online', label: 'Online' }].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setFilterType(opt.value)}
                      className={`px-3 py-1.5 rounded-lg transition-colors ${
                        filterType === opt.value ? 'bg-sedaily-500 text-white' : 'text-text-400 hover:bg-bg-300/50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Sort */}
                <div className="flex items-center gap-1 text-sm">
                  {[{ value: 'time', label: 'Time' }, { value: 'paper', label: 'Page' }, { value: 'title', label: 'Title' }].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setSortBy(opt.value)}
                      className={`px-3 py-1.5 rounded-lg transition-colors ${
                        sortBy === opt.value ? 'bg-sedaily-500 text-white' : 'text-text-400 hover:bg-bg-300/50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Advanced filter toggle */}
                <button
                  onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    activeFilterCount > 0 ? 'bg-sedaily-500/10 text-sedaily-500' : 'text-text-400 hover:bg-bg-300/50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Advanced Filter
                  {activeFilterCount > 0 && (
                    <span className="bg-sedaily-500 text-white text-xs px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>
                  )}
                  {showAdvancedFilter ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>

              {/* Advanced filter panel */}
              <AnimatePresence>
                {showAdvancedFilter && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="max-w-6xl mx-auto px-6 py-4 border-t border-bg-300/30 bg-bg-200/30 space-y-4">
                      {/* Time filter */}
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={useTimeFilter}
                            onChange={(e) => setUseTimeFilter(e.target.checked)}
                            className="w-4 h-4 rounded border-bg-300 text-sedaily-500 focus:ring-sedaily-500"
                          />
                          <Clock className="w-4 h-4 text-text-400" />
                          <span className="text-sm font-medium text-text-200">Time Filter</span>
                        </label>
                        {useTimeFilter && (
                          <div className="flex items-center gap-2">
                            <input
                              type="time"
                              value={startTime}
                              onChange={(e) => setStartTime(e.target.value)}
                              className="px-2 py-1 text-sm border border-bg-300/50 rounded-lg bg-bg-100 text-text-100 focus:outline-none focus:border-sedaily-500/50"
                            />
                            <span className="text-text-500">~</span>
                            <input
                              type="time"
                              value={endTime}
                              onChange={(e) => setEndTime(e.target.value)}
                              className="px-2 py-1 text-sm border border-bg-300/50 rounded-lg bg-bg-100 text-text-100 focus:outline-none focus:border-sedaily-500/50"
                            />
                          </div>
                        )}
                      </div>

                      {/* Exclude categories */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Tag className="w-4 h-4 text-text-400" />
                          <span className="text-sm font-medium text-text-200">Exclude Categories</span>
                          <span className="text-xs text-text-500">({excludeCategories.length} selected)</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {['Economy', 'Finance', 'Industry', 'Real Estate', 'Stocks', 'Politics', 'Sports', 'Entertainment', 'Culture', 'Obituary', 'Health'].map(cat => (
                            <button
                              key={cat}
                              onClick={() => setExcludeCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat])}
                              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                                excludeCategories.includes(cat)
                                  ? 'bg-red-500/10 border-red-500/30 text-red-400'
                                  : 'bg-bg-100 border-bg-300/50 text-text-400 hover:border-text-400'
                              }`}
                            >
                              {excludeCategories.includes(cat) && <X className="w-3 h-3 inline mr-1" />}
                              {cat}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Exclude keywords */}
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-text-200">Exclude Title Keywords</span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="text"
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                            placeholder="Keyword to exclude"
                            className="flex-1 max-w-xs px-3 py-1.5 text-sm border border-bg-300/50 rounded-lg bg-bg-100 text-text-100 placeholder:text-text-500 focus:outline-none focus:border-sedaily-500/50"
                          />
                          <button
                            onClick={addKeyword}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-bg-300/50 text-text-300 rounded-lg hover:bg-bg-300 transition-colors"
                          >
                            <Plus className="w-4 h-4" /> Add
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {excludeKeywords.map(kw => (
                            <span key={kw} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-orange-500/10 border border-orange-500/30 text-orange-400 rounded-full">
                              {kw}
                              <button onClick={() => setExcludeKeywords(prev => prev.filter(k => k !== kw))} className="hover:text-orange-200">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Article list */}
            <div className="max-w-6xl mx-auto px-6 py-6">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-sm font-medium text-text-200">
                    {filteredArticles.length}<span className="text-text-500 font-normal">/{articles.length} articles</span>
                  </span>
                  <span className="text-text-500">|</span>
                  <span className="bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full">Print {categoryStats.paperCount}</span>
                  <span className="bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full">Online {categoryStats.onlineCount}</span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setCurrentStep(3)}
                  disabled={filteredArticles.length === 0}
                  className="flex items-center gap-2 px-5 py-2.5 bg-sedaily-500 text-white text-sm font-medium rounded-xl hover:bg-sedaily-400 transition-all disabled:opacity-50"
                >
                  Select from {filteredArticles.length} articles <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>

              <div className="grid gap-3">
                {filteredArticles.slice(0, 50).map(article => (
                  <div
                    key={article.id}
                    onClick={() => setSelectedArticle(article)}
                    className="bg-bg-200/30 rounded-xl border border-bg-300/30 p-4 hover:border-bg-300/50 hover:bg-bg-200/50 cursor-pointer transition-all group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-text-100 text-sm leading-snug mb-2 line-clamp-2">{article.title}</h3>
                        <div className="flex items-center gap-4 text-xs text-text-500">
                          {article.paperNumber > 0 ? (
                            <span className="flex items-center gap-1 text-blue-400">
                              <FileText className="w-3.5 h-3.5" />p.{article.paperNumber}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-emerald-400">
                              <Newspaper className="w-3.5 h-3.5" />Online
                            </span>
                          )}
                          {article.author && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{article.author}</span>}
                          <span>{article.date} {article.time}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => copyArticle(article, e)}
                        className="flex-shrink-0 p-2 rounded-lg text-text-500 hover:text-text-200 hover:bg-bg-300/50 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="mt-3 text-xs text-text-500 line-clamp-2 leading-relaxed">{article.content?.substring(0, 200)}...</p>
                  </div>
                ))}
              </div>

              {filteredArticles.length > 50 && (
                <p className="text-center text-sm text-text-500 mt-6">+ {filteredArticles.length - 50} more</p>
              )}
            </div>
          </div>
        )}

        {/* ========== STEP 3: Select Articles ========== */}
        {currentStep === 3 && (
          <div className="max-w-5xl mx-auto px-6 py-8">
            {classifyStatus === 'idle' && (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-xl font-semibold text-text-100 mb-2">Select Articles by Audience</h2>
                  <p className="text-sm text-text-400">AI will select key articles from {filteredArticles.length} articles for each target audience</p>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <button onClick={toggleAllTargets} className="text-sm text-sedaily-500 hover:text-sedaily-400 font-medium">
                    {selectedTargets.length === AUDIENCE_TYPES.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-xs text-text-500">{selectedTargets.length}/{AUDIENCE_TYPES.length} selected</span>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-8">
                  {AUDIENCE_TYPES.map(target => {
                    const selected = selectedTargets.includes(target.id);
                    const Icon = target.icon;
                    return (
                      <motion.button
                        key={target.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => toggleTarget(target.id)}
                        className={`
                          p-4 rounded-2xl border-2 text-left transition-all
                          ${selected
                            ? 'border-sedaily-500/50 bg-sedaily-500/10'
                            : 'border-bg-300/30 bg-bg-200/30 hover:border-bg-300/50'
                          }
                        `}
                      >
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${target.gradient} flex items-center justify-center mb-3`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-text-100">{target.name}</span>
                          {selected && <Check className="w-4 h-4 text-sedaily-500" />}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-text-400 hover:text-text-200 border border-bg-300/30 rounded-xl hover:border-bg-300/50 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to Filter
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={runClassification}
                    disabled={selectedTargets.length === 0}
                    className="flex items-center gap-2.5 px-8 py-3 bg-gradient-to-r from-sedaily-500 to-sedaily-600 text-white text-sm font-medium rounded-xl shadow-lg shadow-sedaily-500/25 hover:shadow-xl transition-all disabled:opacity-50"
                  >
                    <Play className="w-4 h-4" /> Start Selection for {selectedTargets.length} targets
                  </motion.button>
                </div>
              </>
            )}

            {/* Classification in progress */}
            {(classifyStatus === 'running' || classifyStatus === 'stopped') && (
              <div>
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-3 mb-3">
                    <Sparkles className="w-5 h-5 text-sedaily-500 animate-pulse" />
                    <h2 className="text-lg font-semibold text-text-100">
                      {classifyStatus === 'running' ? 'AI is analyzing articles' : 'Stopped'}
                    </h2>
                    {classifyStatus === 'running' && <PulseDots />}
                  </div>
                  <div className="flex items-center justify-center gap-3 text-sm text-text-400">
                    <span>{classifiedCount}/{selectedTargets.length} targets done</span>
                    <span className="text-text-500">·</span>
                    <ElapsedTimer running={classifyStatus === 'running'} />
                  </div>
                </div>

                {/* Progress bar */}
                <div className="max-w-lg mx-auto mb-10">
                  <div className="h-1.5 bg-bg-300/50 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${selectedTargets.length > 0 ? (classifiedCount / selectedTargets.length) * 100 : 0}%` }}
                      className="h-full rounded-full bg-gradient-to-r from-sedaily-500 via-sedaily-400 to-sedaily-500 bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite]"
                    />
                  </div>
                </div>

                {/* Target cards */}
                <div className="grid grid-cols-4 gap-4 mb-8">
                  {AUDIENCE_TYPES.filter(t => selectedTargets.includes(t.id)).map(target => {
                    const result = classificationResults[target.id];
                    const isActive = activeClassifyTarget === target.id;
                    const isDone = !!result;
                    const Icon = target.icon;
                    return (
                      <motion.div
                        key={target.id}
                        animate={{ scale: isActive ? 1.02 : 1 }}
                        className={`
                          relative p-5 rounded-2xl border-2 transition-all
                          ${isDone ? 'border-emerald-500/30 bg-emerald-500/10' : ''}
                          ${isActive ? 'border-sedaily-500/50 bg-sedaily-500/10 shadow-lg shadow-sedaily-500/10' : ''}
                          ${!isDone && !isActive ? 'border-bg-300/30 bg-bg-200/30 opacity-60' : ''}
                        `}
                      >
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${target.gradient} flex items-center justify-center mb-3`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-text-100">{target.name}</span>
                          {isDone && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                          {isActive && <Loader2 className="w-4 h-4 text-sedaily-500 animate-spin" />}
                        </div>
                        <div className="text-xs h-5">
                          {isDone && <span className="text-emerald-400 font-medium">{result.selectedIds.length} selected</span>}
                          {isActive && <span className="text-sedaily-500">Analyzing...</span>}
                          {!isDone && !isActive && <span className="text-text-500">Waiting</span>}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <div className="text-center">
                  {classifyStatus === 'running' && (
                    <button
                      onClick={handleStop}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm text-text-400 hover:text-text-200 border border-bg-300/30 rounded-xl hover:border-bg-300/50 transition-colors"
                    >
                      <Square className="w-3.5 h-3.5" /> Stop
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Classification complete */}
            {classifyStatus === 'done' && (
              <>
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 mb-4">
                    <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-text-100 mb-1">Selection Complete</h2>
                  <p className="text-sm text-text-400">Articles selected for {classifiedCount} targets</p>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-8">
                  {AUDIENCE_TYPES.filter(t => selectedTargets.includes(t.id)).map(target => {
                    const result = classificationResults[target.id];
                    const Icon = target.icon;
                    return (
                      <div key={target.id} className="p-5 rounded-2xl bg-bg-200/30 border border-bg-300/30">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${target.gradient} flex items-center justify-center mb-3`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-sm font-medium text-text-100 mb-1">{target.name}</div>
                        <div className="text-xs text-emerald-400 font-medium">{result?.selectedIds?.length || 0} selected</div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={() => { setClassifyStatus('idle'); setClassificationResults({}); }}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-text-400 hover:text-text-200 border border-bg-300/30 rounded-xl hover:border-bg-300/50 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" /> Re-select
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setCurrentStep(4)}
                    className="flex items-center gap-2.5 px-8 py-3 bg-gradient-to-r from-sedaily-500 to-sedaily-600 text-white text-sm font-medium rounded-xl shadow-lg shadow-sedaily-500/25 transition-all"
                  >
                    Proceed to Generate Content <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ========== STEP 4: Generate Content ========== */}
        {currentStep === 4 && generateStatus !== 'done' && (
          <div className="max-w-5xl mx-auto px-6 py-8">
            {generateStatus === 'idle' && (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-xl font-semibold text-text-100 mb-2">Generate Content</h2>
                  <p className="text-sm text-text-400">Generate longform and AEO content based on selected articles</p>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-8">
                  {AUDIENCE_TYPES.filter(t => selectedTargets.includes(t.id)).map(target => {
                    const data = classificationResults[target.id];
                    const count = data?.selectedIds?.length || 0;
                    const Icon = target.icon;
                    return (
                      <div key={target.id} className={`p-5 rounded-2xl border ${count > 0 ? 'bg-bg-200/30 border-bg-300/30' : 'bg-bg-200/10 border-bg-300/20 opacity-50'}`}>
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${target.gradient} flex items-center justify-center mb-3`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-sm font-medium text-text-100">{target.name}</div>
                        <div className="text-xs text-text-500 mt-1">{count} articles selected</div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-text-400 hover:text-text-200 border border-bg-300/30 rounded-xl hover:border-bg-300/50 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to Selection
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={runGeneration}
                    className="flex items-center gap-2.5 px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium rounded-xl shadow-lg shadow-amber-500/25 transition-all"
                  >
                    <Zap className="w-4 h-4" /> Start Content Generation
                  </motion.button>
                </div>
              </>
            )}

            {/* Generation in progress */}
            {(generateStatus === 'running' || generateStatus === 'stopped') && (
              <div>
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-3 mb-3">
                    <Zap className="w-5 h-5 text-amber-500 animate-pulse" />
                    <h2 className="text-lg font-semibold text-text-100">
                      {generateStatus === 'running' ? 'Generating content' : 'Stopped'}
                    </h2>
                    {generateStatus === 'running' && <PulseDots />}
                  </div>
                  <div className="flex items-center justify-center gap-3 text-sm text-text-400">
                    <span>{generatedCount}/{selectedTargets.filter(t => classificationResults[t]?.selectedIds?.length > 0).length} targets done</span>
                    <span className="text-text-500">·</span>
                    <ElapsedTimer running={generateStatus === 'running'} />
                  </div>
                </div>

                <div className="max-w-lg mx-auto mb-10">
                  <div className="h-1.5 bg-bg-300/50 rounded-full overflow-hidden">
                    <motion.div
                      animate={{
                        width: `${(() => {
                          const total = selectedTargets.filter(t => classificationResults[t]?.selectedIds?.length > 0).length;
                          return total > 0 ? (generatedCount / total) * 100 : 0;
                        })()}%`
                      }}
                      className="h-full rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-amber-400 bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-8">
                  {AUDIENCE_TYPES.filter(t => selectedTargets.includes(t.id) && classificationResults[t.id]?.selectedIds?.length > 0).map(target => {
                    const result = generationResults[target.id];
                    const isActive = activeGenTarget === target.id;
                    const isDone = !!result;
                    const Icon = target.icon;
                    return (
                      <motion.div
                        key={target.id}
                        animate={{ scale: isActive ? 1.02 : 1 }}
                        className={`
                          relative p-5 rounded-2xl border-2 transition-all
                          ${isDone ? 'border-emerald-500/30 bg-emerald-500/10' : ''}
                          ${isActive ? 'border-amber-500/50 bg-amber-500/10 shadow-lg shadow-amber-500/10' : ''}
                          ${!isDone && !isActive ? 'border-bg-300/30 bg-bg-200/30 opacity-60' : ''}
                        `}
                      >
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${target.gradient} flex items-center justify-center mb-3`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-text-100">{target.name}</span>
                          {isDone && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                          {isActive && <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />}
                        </div>
                        <div className="text-xs h-5">
                          {isDone && <span className="text-emerald-400 font-medium">Longform + AEO done</span>}
                          {isActive && <span className="text-amber-500">Generating...</span>}
                          {!isDone && !isActive && <span className="text-text-500">Waiting</span>}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <div className="text-center">
                  {generateStatus === 'running' && (
                    <button
                      onClick={handleStop}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm text-text-400 hover:text-text-200 border border-bg-300/30 rounded-xl hover:border-bg-300/50 transition-colors"
                    >
                      <Square className="w-3.5 h-3.5" /> Stop
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== STEP 5: Results ========== */}
        {(currentStep === 5 || (currentStep === 4 && generateStatus === 'done')) && (
          <div className="max-w-6xl mx-auto px-6 py-8">
            {currentStep === 4 && generateStatus === 'done' && (() => { setTimeout(() => setCurrentStep(5), 0); return null; })()}

            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-text-100">Results</h2>
                <p className="text-sm text-text-400 mt-1">{generatedCount} targets completed</p>
              </div>
              <button
                onClick={resetAll}
                className="flex items-center gap-2 text-sm text-text-400 hover:text-text-200 transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> Start Over
              </button>
            </div>

            {/* Target selection */}
            <div className="grid grid-cols-4 gap-3 mb-8">
              {AUDIENCE_TYPES.filter(t => selectedTargets.includes(t.id)).map(target => {
                const result = generationResults[target.id];
                const isSelected = selectedTarget === target.id;
                const Icon = target.icon;
                return (
                  <motion.button
                    key={target.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedTarget(isSelected ? null : target.id)}
                    disabled={!result}
                    className={`
                      relative p-4 rounded-2xl text-left transition-all disabled:opacity-40
                      ${isSelected
                        ? 'bg-gradient-to-br from-sedaily-500 to-sedaily-600 text-white shadow-xl shadow-sedaily-500/25'
                        : 'bg-bg-200/30 text-text-100 border border-bg-300/30 hover:border-bg-300/50'
                      }
                    `}
                  >
                    <div className={`w-10 h-10 rounded-xl ${isSelected ? 'bg-white/20' : `bg-gradient-to-br ${target.gradient}`} flex items-center justify-center mb-2`}>
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-white'}`} />
                    </div>
                    <div className="font-medium text-sm mb-1">{target.name}</div>
                    <div className={`text-xs ${isSelected ? 'text-white/60' : 'text-text-500'}`}>
                      {result ? `${result.selectedIds?.length || 0} articles · Done` : 'No articles selected'}
                    </div>
                    {result && !isSelected && <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-emerald-500" />}
                  </motion.button>
                );
              })}
            </div>

            {/* 2-column results layout */}
            {selectedTarget && currentResult && (
              <div className="grid grid-cols-2 gap-6">
                {/* Left: Selected articles */}
                <div className="bg-bg-200/30 rounded-2xl border border-bg-300/30 overflow-hidden">
                  <div className="px-5 py-4 border-b border-bg-300/30">
                    <h3 className="font-medium text-text-100 text-sm">{currentTargetInfo?.name} Selected Articles</h3>
                    <p className="text-xs text-text-500 mt-0.5">{currentResult.selectedArticles?.length || 0} articles</p>
                  </div>
                  <div className="divide-y divide-bg-300/20 max-h-[400px] overflow-y-auto">
                    {currentResult.selectedArticles?.map(article => (
                      <div
                        key={article.id}
                        onClick={() => setSelectedArticle(article)}
                        className="px-5 py-3 hover:bg-bg-300/30 cursor-pointer transition-colors"
                      >
                        <p className="text-sm text-text-100 line-clamp-1">{article.title}</p>
                        <p className="text-xs text-text-500 mt-1">{article.author} · {article.date}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Content */}
                <div className="bg-bg-200/30 rounded-2xl border border-bg-300/30 overflow-hidden flex flex-col">
                  <div className="px-5 py-4 border-b border-bg-300/30 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {['longform', 'aeo'].map(tab => (
                        <button
                          key={tab}
                          onClick={() => setContentTab(tab)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            contentTab === tab ? 'bg-sedaily-500 text-white' : 'text-text-400 hover:text-text-200'
                          }`}
                        >
                          {tab === 'longform' ? 'Longform' : 'AEO'}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => handleCopyContent(contentTab === 'longform' ? currentResult.longform : currentResult.aeo)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-text-400 hover:text-text-200 hover:bg-bg-300/50 rounded-lg transition-colors"
                    >
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-5 max-h-[400px]">
                    {(contentTab === 'longform' ? currentResult.longform : currentResult.aeo) ? (
                      <pre className="text-sm text-text-200 whitespace-pre-wrap font-sans leading-relaxed">
                        {contentTab === 'longform' ? currentResult.longform : currentResult.aeo}
                      </pre>
                    ) : (
                      <div className="h-full flex items-center justify-center text-text-500 text-sm">No content</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Article modal */}
      <AnimatePresence>
        {selectedArticle && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedArticle(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-bg-200 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden shadow-2xl border border-bg-300/30 flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-bg-300/30 flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs text-text-500">
                  {selectedArticle.paperNumber > 0 ? (
                    <span className="flex items-center gap-1 bg-blue-500/10 text-blue-400 px-2 py-1 rounded">
                      <FileText className="w-3.5 h-3.5" />p.{selectedArticle.paperNumber}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded">
                      <Newspaper className="w-3.5 h-3.5" />Online
                    </span>
                  )}
                  <span>{selectedArticle.date}</span>
                  {selectedArticle.time && <span>{selectedArticle.time}</span>}
                </div>
                <button onClick={() => setSelectedArticle(null)} className="p-1.5 hover:bg-bg-300/50 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-text-400" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <h2 className="text-xl font-semibold text-text-100 mb-3 leading-tight">{selectedArticle.title}</h2>
                {selectedArticle.author && (
                  <p className="text-sm text-text-400 mb-4 flex items-center gap-1.5">
                    <User className="w-4 h-4" />{selectedArticle.author}
                  </p>
                )}
                <div className="border-t border-bg-300/30 pt-6">
                  <p className="text-sm text-text-200 leading-relaxed whitespace-pre-wrap">{selectedArticle.content}</p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-bg-300/30 flex items-center justify-end gap-2 bg-bg-200/50">
                <button
                  onClick={() => copyArticle(selectedArticle)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-text-300 hover:text-text-100 bg-bg-300/50 border border-bg-300/30 rounded-lg hover:border-bg-300/50 transition-colors"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="px-4 py-2 text-sm bg-sedaily-500 text-white rounded-lg hover:bg-sedaily-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Styles */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
};

export default PrismCanvas;
